import { getInitials } from '@frontend/api-client';
import type { Client, Household } from '@frontend/api-types';
import { dayjs, match } from '@frontend/duck-tape';
import { useVisibilityController } from '@frontend/react';
import { Avatar, Badge, Button, ConfirmationModal, LinkButton, Text, XStack, YStack } from '@frontend/web-react';
import { InviteHouseholdMemberModal } from '@frontend/web/components';
import { useExpireInviteMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useState } from 'react';

type HouseholdMembersSectionProps = {
  client: Client;
  household: Household;
};

type HouseholdMemberListItem = {
  displayName: string;
  id: string;
  lastName: string;
};

type HouseholdInvite = NonNullable<Household['openInvites']>[number];

type HouseholdInviteRowProps = {
  canRemoveInvite: boolean;
  invite: HouseholdInvite;
  onRemoveInvite: (invite: HouseholdInvite) => void;
};

type HouseholdMemberRowProps = {
  householdOwnerId: Household['ownerId'];
  member: HouseholdMemberListItem;
  viewerId: Client['id'];
};

const HouseholdMemberRow = ({ householdOwnerId, member, viewerId }: HouseholdMemberRowProps) => (
  <XStack className="items-center gap-md p-lg">
    <Avatar
      initials={getInitials({ firstName: member.displayName, lastName: member.lastName, nickname: '' })}
      size="md"
    />
    <XStack className="flex-1 items-center gap-sm">
      <Text type="p-md">
        {member.displayName} {member.lastName}
      </Text>
      {member.id === householdOwnerId ? (
        <Badge color="primary" uppercase>
          OWNER
        </Badge>
      ) : null}
    </XStack>
    {member.id === viewerId ? (
      <Text className="text-textTertiary" type="p-sm">
        You
      </Text>
    ) : null}
  </XStack>
);

const getInviteFullName = ({ firstName, lastName }: Pick<HouseholdInvite, 'firstName' | 'lastName'>) =>
  `${firstName} ${lastName}`.trim();

const getInviteStatus = (invite: HouseholdInvite) =>
  match(dayjs(invite.expirationDate).isBefore(dayjs().startOf('day'), 'day'))
    .with(true, () => ({ badgeColor: 'danger' as const, label: 'EXPIRED' }))
    .with(false, () => ({ badgeColor: 'secondary' as const, label: 'PENDING' }))
    .exhaustive();

const HouseholdInviteRow = ({ canRemoveInvite, invite, onRemoveInvite }: HouseholdInviteRowProps) => {
  const inviteStatus = getInviteStatus(invite);
  const inviteFullName = getInviteFullName(invite);

  return (
    <XStack className="items-center gap-md p-lg">
      <Avatar
        initials={getInitials({ firstName: invite.firstName, lastName: invite.lastName, nickname: '' })}
        size="md"
      />
      <XStack className="flex-1 items-center justify-between gap-md">
        <XStack className="min-w-1 flex-1 flex-wrap items-center gap-sm">
          <Text type="p-md">{inviteFullName}</Text>
          <Badge color={inviteStatus.badgeColor} uppercase>
            {inviteStatus.label}
          </Badge>
        </XStack>
        {canRemoveInvite ? (
          <LinkButton
            accessibilityLabel={`Remove invite for ${inviteFullName}`}
            className="shrink-0"
            color="danger"
            label=""
            leftIconName="IconTrash"
            leftIconProps={{ size: 'md' }}
            onClick={() => onRemoveInvite(invite)}
            type="h6"
          />
        ) : null}
      </XStack>
    </XStack>
  );
};

export const HouseholdMembersSection = ({ client, household }: HouseholdMembersSectionProps) => {
  const [expireInvite, { isLoading: isExpireInviteLoading }] = useExpireInviteMutation();
  const [isInviteModalOpen, { close: closeInviteModal, open: openInviteModal }] = useVisibilityController();
  const [isRemoveInviteModalOpen, { close: closeRemoveInviteModal, open: openRemoveInviteModal }] =
    useVisibilityController();
  const [inviteToRemove, setInviteToRemove] = useState<HouseholdInvite | null>(null);
  const apiMembers: HouseholdMemberListItem[] = (household.members ?? []).map(({ id, lastName, name }) => ({
    displayName: name,
    id,
    lastName,
  }));
  const currentUserInMembers = apiMembers.some(({ id }) => id === client.id);
  const canInviteMembers = client.isHouseholdOwner === true;
  const currentUserEntry: HouseholdMemberListItem = {
    displayName: client.nickname || client.firstName,
    id: client.id,
    lastName: client.lastName,
  };
  const members = currentUserInMembers ? apiMembers : [currentUserEntry, ...apiMembers];
  const openInvites = household.openInvites ?? [];
  const isHouseholdFull = (household.maxUsers ?? 0) > 0 && (household.currentUsers ?? 0) >= (household.maxUsers ?? 0);

  const handleCloseRemoveInviteModal = () => {
    closeRemoveInviteModal();
    setInviteToRemove(null);
  };

  const handleOpenRemoveInviteModal = (invite: HouseholdInvite) => {
    setInviteToRemove(invite);
    openRemoveInviteModal();
  };

  const handleConfirmRemoveInvite = () => {
    if (!inviteToRemove) {
      return;
    }

    handleMutation({
      mutation: () => expireInvite(inviteToRemove.id).unwrap(),
      onSuccess: handleCloseRemoveInviteModal,
      successMessage: 'Invite removed successfully',
    });
  };

  return (
    <>
      <YStack className="gap-md">
        <XStack className="items-center justify-between">
          <Text type="h5">Members</Text>
          {canInviteMembers ? (
            <Button
              disabled={isHouseholdFull}
              label="+ Invite member"
              onClick={openInviteModal}
              size="sm"
              variant="primary"
            />
          ) : null}
        </XStack>
        <YStack className="gap-sm divide-y divide-solid divide-borderTertiary rounded-lg border-1 border-borderTertiary">
          {members.length === 0 && openInvites.length === 0 ? (
            <Text className="p-lg text-textTertiary" type="p-md">
              No members yet. Invite someone to get started.
            </Text>
          ) : (
            <>
              {members.map((member) => (
                <HouseholdMemberRow
                  householdOwnerId={household.ownerId}
                  key={member.id}
                  member={member}
                  viewerId={client.id}
                />
              ))}
              {openInvites.map((invite) => (
                <HouseholdInviteRow
                  canRemoveInvite={canInviteMembers}
                  invite={invite}
                  key={invite.id}
                  onRemoveInvite={handleOpenRemoveInviteModal}
                />
              ))}
            </>
          )}
        </YStack>
        {isHouseholdFull ? (
          <Text className="text-textTertiary" type="p-sm">
            Your household is full ({household.currentUsers} of {household.maxUsers} members).
          </Text>
        ) : null}
        {canInviteMembers && household.id ? (
          <InviteHouseholdMemberModal
            householdId={household.id}
            isOpen={isInviteModalOpen}
            onClose={closeInviteModal}
          />
        ) : null}
      </YStack>
      <ConfirmationModal
        buttonsOrientation="horizontal"
        cancelLabel="Cancel"
        hideBodyTitle
        isOpen={isRemoveInviteModalOpen}
        isSubmitLoading={isExpireInviteLoading}
        onClickCancel={handleCloseRemoveInviteModal}
        onClickSubmit={handleConfirmRemoveInvite}
        onClose={handleCloseRemoveInviteModal}
        showHeaderTitle
        submitLabel="Remove invite"
        subtitle={`This action cannot be undone. Are you sure you want to remove the invite for ${getInviteFullName(inviteToRemove ?? { firstName: '', lastName: '' })}?`}
        title="Remove invite"
        variant="destructive"
      />
    </>
  );
};
